import type { DietGoal, DietReward } from "@couples/shared";
import { useState } from "react";

import { todayDateKey } from "../lib/dates";
import type { DietGoalInput } from "../lib/diet/goals";
import type { DietRewardInput } from "../lib/diet/reward";
import SheetShell from "./SheetShell";

interface Props {
  selfGoal: DietGoal | null;
  /** 相手の目標。読むだけで、ここからは変えられない */
  partnerGoal: DietGoal | null;
  partnerName: string;
  reward: DietReward | null;
  /** いま二人合わせて減った量(kg)。ごほうびの残りを見せるのに使う */
  totalLoss: number;
  busy: boolean;
  onSave: (goal: DietGoalInput, reward: DietRewardInput) => void;
  onClose: () => void;
}

const inputClass =
  "w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none";

const labelClass = "mb-1 block text-[11px] text-[var(--color-sub)]";

/**
 * 目標とごほうびのシート。
 *
 * ごほうびは共同の約束なのでどちらでも決め直せるが、目標体重は本人だけが決める。
 * 相手に書き換えられると「自分で決めた目標」でなくなり、続ける理由が外から来てしまう。
 */
export default function DietGoalSheet({
  selfGoal,
  partnerGoal,
  partnerName,
  reward,
  totalLoss,
  busy,
  onSave,
  onClose,
}: Props) {
  const [startWeight, setStartWeight] = useState(
    selfGoal === null ? "" : String(selfGoal.startWeight),
  );
  const [goalWeight, setGoalWeight] = useState(
    selfGoal === null ? "" : String(selfGoal.goalWeight),
  );
  const [dueOn, setDueOn] = useState(selfGoal?.dueOn ?? "");
  const [title, setTitle] = useState(reward?.title ?? "");
  const [targetLoss, setTargetLoss] = useState(reward === null ? "" : String(reward.targetLoss));

  const startValue = Number(startWeight);
  const goalValue = Number(goalWeight);
  const lossValue = Number(targetLoss);

  const weightsValid =
    startWeight.trim() !== "" &&
    goalWeight.trim() !== "" &&
    Number.isFinite(startValue) &&
    Number.isFinite(goalValue) &&
    startValue > 0 &&
    startValue < 500 &&
    goalValue > 0 &&
    goalValue < 500;
  const rewardValid =
    title.trim() !== "" && targetLoss.trim() !== "" && Number.isFinite(lossValue) && lossValue > 0;
  const canSubmit = weightsValid && rewardValid;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    onSave(
      {
        startWeight: startValue,
        goalWeight: goalValue,
        startedOn: selfGoal?.startedOn ?? todayDateKey(),
        dueOn: dueOn === "" ? null : dueOn,
      },
      { title: title.trim(), targetLoss: lossValue },
    );
  }

  return (
    <SheetShell title="目標とごほうび" onClose={onClose} tone="bg">
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto p-4">
          <div className="flex flex-col gap-3 rounded-xl border border-[var(--color-line)] bg-white p-4">
            <span className="text-[11px] font-semibold text-[var(--color-sub)]">
              ふたりのごほうび
            </span>

            <label className="block">
              <span className={labelClass}>達成したらすること</span>
              <input
                type="text"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
                placeholder="例: 焼肉食べ放題"
                className={inputClass}
              />
            </label>

            <label className="block">
              <span className={labelClass}>ふたり合わせて減らす量(kg)</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0.1"
                value={targetLoss}
                onChange={(event) => {
                  setTargetLoss(event.target.value);
                }}
                placeholder="例: 8"
                className={inputClass}
              />
            </label>

            <span className="text-[11px] text-[var(--color-sub)]">
              いま ふたり合わせて {totalLoss.toFixed(1)}kg 減りました。
            </span>
          </div>

          <div className="flex flex-col gap-3 rounded-xl border border-[var(--color-line)] bg-white p-4">
            <span className="text-[11px] font-semibold text-[var(--color-sub)]">自分の目標</span>

            <div className="flex gap-2.5">
              <label className="flex-1">
                <span className={labelClass}>開始体重(kg)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={startWeight}
                  onChange={(event) => {
                    setStartWeight(event.target.value);
                  }}
                  placeholder="61.0"
                  className={inputClass}
                />
              </label>
              <label className="flex-1">
                <span className={labelClass}>目標体重(kg)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={goalWeight}
                  onChange={(event) => {
                    setGoalWeight(event.target.value);
                  }}
                  placeholder="57.0"
                  className={inputClass}
                />
              </label>
            </div>

            <label className="block">
              <span className={labelClass}>期限(任意)</span>
              <input
                type="date"
                value={dueOn}
                onChange={(event) => {
                  setDueOn(event.target.value);
                }}
                className={inputClass}
              />
            </label>
          </div>

          {/* 相手の目標は読むだけ。数字を書き換えられると「決めさせられた」になる */}
          <div className="flex flex-col gap-1.5 rounded-xl border border-[var(--color-line)] bg-white p-4">
            <span className="text-[11px] font-semibold text-[var(--color-sub)]">
              {partnerName}の目標
            </span>
            {partnerGoal === null ? (
              <span className="text-sm text-[var(--color-sub)]">まだ決めていません。</span>
            ) : (
              <>
                <span className="text-sm tabular-nums">
                  {partnerGoal.startWeight.toFixed(1)}kg → {partnerGoal.goalWeight.toFixed(1)}kg
                </span>
                <span className="text-[11px] text-[var(--color-sub)]">
                  {partnerGoal.dueOn === null
                    ? "期限なし"
                    : `期限 ${partnerGoal.dueOn.replaceAll("-", "/")}`}
                  (本人だけが変えられます)
                </span>
              </>
            )}
          </div>
        </div>

        <div className="border-t border-[var(--color-line)] p-4">
          <button
            type="submit"
            disabled={!canSubmit || busy}
            className="w-full rounded-lg bg-[var(--color-accent)] py-2.5 text-sm font-medium text-white transition enabled:hover:opacity-85 disabled:opacity-50"
          >
            保存
          </button>
        </div>
      </form>
    </SheetShell>
  );
}
